import * as React from 'react';

import { useChat } from '@ai-sdk/react';
import { useApolloClient } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ASK_CLARIFYING_QUESTIONS_TOOL,
  CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL,
  COMPOSE_EMAIL_TOOL,
  type ComposeEmailOutput,
  READ_CURRENT_PAGE_TOOL,
  type ReadCurrentPageOutput,
} from '@realadvisor/common-types';
import { NESTED_FILE_DROPZONE_ATTR } from '@realadvisor/hooks';
import { captureException } from '@sentry/react';
import { DefaultChatTransport, type UIMessage } from 'ai';
import {
  ChevronDown,
  Compass,
  FolderOpen,
  History,
  Paperclip,
  SquarePen,
  X,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStickToBottom } from 'use-stick-to-bottom';

import {
  ADD_PROPERTY_IMAGES_TOOL_NAME,
  ChatAttachmentPreviewProvider,
  hasUnresolvedPropertyImageAttachments,
  waitsForOriginalUploads,
} from '@modules/_shared/components/chat/AddPropertyImagesToolCard';
import { uploadAttachmentOriginal } from '@modules/_shared/components/chat/chatAttachmentOriginal';
import {
  type ChatFileUIPart,
  type ProcessedAttachment,
  fileToFileUIPart,
  isAcceptedFile,
  isTooManyPhotosError,
  validateAttachments,
} from '@modules/_shared/components/chat/chatAttachments';
import {
  chatErrorMessage,
  parseChatError,
} from '@modules/_shared/components/chat/chatErrors';
import {
  chatApiBase,
  chatFetch,
  getChatHeaders,
} from '@modules/_shared/components/chat/chatFetch';
import { ChatFiles } from '@modules/_shared/components/chat/ChatFiles';
import { ChatHistory } from '@modules/_shared/components/chat/ChatHistory';
import { ChatInput } from '@modules/_shared/components/chat/ChatInput';
import {
  ChatMessage,
  type MessagePart,
  ThinkingIndicator,
  getMessageText,
} from '@modules/_shared/components/chat/ChatMessage';
import {
  ChatMessageActions,
  type ChatMessageMetadata,
} from '@modules/_shared/components/chat/ChatMessageActions';
import { useChatPanel } from '@modules/_shared/components/chat/ChatPanelContext';
import {
  readChatSessionUserId,
  readSessionChatId,
  writeSessionChatId,
} from '@modules/_shared/components/chat/chatPanelDefaults';
import {
  headerActionsEnabled,
  settledToolResultCount,
  waitsForUser,
} from '@modules/_shared/components/chat/chatPanelViews';
import {
  claimRefreshIds,
  composeEmailRefreshIds,
  extractRecordId,
  isNavigateNoop,
  isSuccessfulMutationPart,
  isThemeWriteSuccess,
  queryVariablesMentionAnyRecord,
} from '@modules/_shared/components/chat/chatRecordRefresh';
import { ChatTurnStartContext } from '@modules/_shared/components/chat/ChatSteps';
import { ChatTableViewerProvider } from '@modules/_shared/components/chat/ChatTableViewer';
import {
  type ChatTurn,
  groupChatTurns,
  isCutOffReply,
} from '@modules/_shared/components/chat/chatTurns';
import { type ClarifyResolveHandler } from '@modules/_shared/components/chat/clarifyAnswers';
import { ClarifyingQuestionsDock } from '@modules/_shared/components/chat/ClarifyingQuestionsCard';
import {
  buildChatClientContext,
  resolveNavigatePath,
} from '@modules/_shared/components/chat/clientTools';
import { readCurrentPage } from '@modules/_shared/components/chat/currentPage';
import {
  findPendingClarifyPart,
  isPendingComposeEmailPart,
} from '@modules/_shared/components/chat/messageSegments';
import { ModelSelector } from '@modules/_shared/components/chat/ModelSelector';
import { createOriginalUploadQueue } from '@modules/_shared/components/chat/originalUploadQueue';
import {
  createEarlyLayoutChecks,
  runTemplateLayoutCheck,
  stoppedLayoutCheckOutput,
  unsettledLayoutCheckIds,
} from '@modules/_shared/components/chat/templateLayoutCheck';
import {
  agentLoopMayContinue,
  interruptedRetriesAfterSettle,
  mayAutoApprove,
  messagesWithSettledResults,
  unsupportedClientToolResults,
} from '@modules/_shared/components/chat/unsupportedClientTool';
import { useModels } from '@modules/_shared/components/chat/useModels';
import { VisualChatIdProvider } from '@modules/_shared/components/chat/VisualToolCard';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { useLocale } from '@shared/hooks/locale';
import { TOP_BAR_HEIGHT } from '@shared/theme/layout';
import { useTheme } from '@shared/theme/theme';

const toolNameOfPart = (part: MessagePart): string =>
  part.type === 'dynamic-tool'
    ? part.toolName ?? ''
    : part.type.slice('tool-'.length);

const isToolPart = (part: MessagePart): boolean =>
  part.type.startsWith('tool-') || part.type === 'dynamic-tool';

const abortLayoutChecks = (checks: Map<string, AbortController>) => {
  for (const check of checks.values()) {
    check.abort();
  }
  checks.clear();
};

const refetchQueriesMentioning = (
  apolloClient: ReturnType<typeof useApolloClient>,
  ids: string[],
) => {
  if (ids.length === 0) {
    return;
  }
  void apolloClient
    .refetchQueries({
      include: 'active',
      onQueryUpdated: observable =>
        queryVariablesMentionAnyRecord(observable.variables, ids),
    })
    .catch(() => {
      // Best effort: a failed refresh leaves the previous data on screen.
    });
};

// A chat API error: the translated message for its code, with the technical
// detail in small print so a screenshot stays actionable. Uncoded errors
// (older API, network) are shown raw, as before.
const ChatErrorText = ({ message }: { message: string }) => {
  const { t } = useLocale();
  const { code, detail } = parseChatError(message);
  if (code == null) {
    return <>{message || t('An error occurred')}</>;
  }
  return (
    <>
      {chatErrorMessage(code, t)}
      {detail !== '' && (
        <Typography
          variant="caption"
          component="div"
          sx={{ mt: 0.5, opacity: 0.7, wordBreak: 'break-word' }}
        >
          {detail}
        </Typography>
      )}
    </>
  );
};

export const ChatPanelInner = ({
  onClose,
  isMobile = false,
  tableContainerRef,
}: {
  onClose: () => void;
  isMobile?: boolean;
  /** Where expanded tables open, beside the chat. Full screen when absent. */
  tableContainerRef?: React.RefObject<HTMLDivElement | null>;
}) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const { messagesRef, chatIdRef, modelIdRef } = useChatPanel();

  // Keep navigate in a ref so the (memoised) onToolCall closure always calls the
  // current router instance.
  const navigate = useNavigate();
  const navigateRef = React.useRef(navigate);
  navigateRef.current = navigate;
  // Same for the current location (basename-relative) and the Apollo client:
  // read at call time from the `useChat` closure and the settle effect below.
  const location = useLocation();
  const locationRef = React.useRef(location);
  locationRef.current = location;
  const apolloClient = useApolloClient();

  const { scrollRef, contentRef, isAtBottom, scrollToBottom } =
    useStickToBottom();
  const inputRef = React.useRef<{ focus: () => void }>(null);
  const focusInput = () => {
    if (!isMobile) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const [input, setInput] = React.useState('');
  const [files, setFiles] = React.useState<ChatFileUIPart[]>([]);
  // Full-quality originals of the queued resized photos, keyed by
  // attachmentId. Kept out of `files` so they are never serialized into a
  // message.
  const queuedOriginalsRef = React.useRef(new Map<string, File>());
  const [originalUploads] = React.useState(() =>
    createOriginalUploadQueue<File>({ upload: uploadAttachmentOriginal }),
  );
  const pendingOriginalIds = React.useSyncExternalStore(
    originalUploads.subscribe,
    originalUploads.getPendingIds,
  );
  const [attachError, setAttachError] = React.useState<string | null>(null);
  const [view, setView] = React.useState<'chat' | 'history' | 'files'>('chat');
  // "Approve all" mode: auto-approve every mutation approval request for the
  // rest of this chat session. Deliberately NOT persisted — it resets on a new
  // chat, on loading another chat, and on remount, so standing consent never
  // outlives the session that granted it.
  const [autoApprove, setAutoApprove] = React.useState(false);
  // Approval ids already answered automatically, so a re-render can't respond
  // to the same request twice.
  const autoRespondedApprovalsRef = React.useRef<Set<string>>(new Set());
  // Tool-call ids already answered with the "unsupported client tool" fallback,
  // so the settle-time safety net responds to each at most once.
  const unsupportedToolRespondedRef = React.useRef<Set<string>>(new Set());
  // compose_email calls already answered from their card (sent / discarded /
  // dismissed), so a remounted card can't answer the same call twice.
  const composeEmailRespondedRef = React.useRef<Set<string>>(new Set());
  // Starts set so opening the panel or reloading does not resume a dangling
  // tool. Also set by Stop, New chat, and opening another chat. The settle
  // effect still marks a dangling server tool interrupted, but
  // `agentLoopMayContinue` refuses the automatic next turn. Cleared when the
  // user sends, edits, regenerates, or answers an approval.
  const userStoppedRef = React.useRef(true);
  // Automatic retries spent after an interrupted settle since the last user
  // action. One is allowed. A later drop in that same stretch waits, even
  // when it is not the very next step. Reset in `allowAgentResume`.
  const interruptedAutoRetriesRef = React.useRef(0);
  // Clarifying-question tool calls already answered from their card, so a
  // double click or a remounted card can't resolve the same call twice.
  const clarifyRespondedRef = React.useRef<Set<string>>(new Set());
  // Mutation tool-call ids whose on-screen refresh already ran, plus whether the
  // previous render was mid-stream — the refresh effect only reacts to a stream
  // settling, never to history loads / chat switches replaying old parts.
  const refreshedMutationCallsRef = React.useRef<Set<string>>(new Set());
  const liveRefreshedCallsRef = React.useRef<Set<string>>(new Set());
  const wasLoadingRef = React.useRef(false);
  const layoutChecksRef = React.useRef(new Map<string, AbortController>());
  const startedLayoutChecksRef = React.useRef(new Set<string>());
  const earlyLayoutChecksRef = React.useRef(createEarlyLayoutChecks());
  const [layoutChecksRunning, setLayoutChecksRunning] = React.useState(false);

  const {
    models,
    selectedModelId,
    selectedModelRef,
    handleModelChange,
    selectLoadedModel,
    resetToDefaultModel,
    modelsLoading,
    modelsError,
  } = useModels(modelIdRef);

  const [sessionUserId] = React.useState(readChatSessionUserId);

  const [chatId, setChatId] = React.useState<string>(() => {
    const fromRef = chatIdRef.current;
    if (fromRef) {
      return fromRef;
    }
    const fromSession = readSessionChatId(sessionUserId);
    if (fromSession) {
      chatIdRef.current = fromSession;
      return fromSession;
    }
    const id = crypto.randomUUID();
    chatIdRef.current = id;
    return id;
  });

  React.useEffect(() => {
    writeSessionChatId(sessionUserId, chatId);
  }, [chatId, sessionUserId]);

  const transport = React.useMemo(
    () =>
      new DefaultChatTransport({
        api: chatApiBase,
        headers: (): Record<string, string> => getChatHeaders(),
        fetch: chatFetch,
        prepareSendMessagesRequest: ({ id, messages: msgs }) => ({
          body: {
            id,
            message: msgs[msgs.length - 1],
            model: selectedModelRef.current,
            // Recomputed per request so url/time reflect the user's current state.
            context: buildChatClientContext(),
          },
        }),
        prepareReconnectToStreamRequest: ({ id }) => ({
          api: `${chatApiBase}/${id}/stream`,
          headers: getChatHeaders(),
        }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    messages,
    sendMessage,
    status,
    error,
    setMessages,
    stop,
    regenerate,
    addToolResult,
    addToolApprovalResponse,
  } = useChat({
    id: chatId,
    generateId: () => crypto.randomUUID(),
    transport,
    resume: true,
    // Resume the agent loop automatically once client tool calls have results
    // or every pending tool approval has been answered. Read-only: the retry
    // count is updated in the settle effect, because the SDK also calls this
    // when a request finishes.
    sendAutomaticallyWhen: ({ messages: currentMessages }) =>
      agentLoopMayContinue(
        currentMessages,
        userStoppedRef.current,
        interruptedAutoRetriesRef.current,
      ),
    onToolCall: ({ toolCall }) => {
      if (toolCall.toolName === 'navigate_to') {
        const result = resolveNavigatePath(toolCall.input);
        // Already on that page and the destination carries no new query
        // string: a router push would only add a history entry and drop the
        // list's `?view=`/`?where=`. Filter deep-links (`/leads?where=…`) and
        // tab changes still navigate. Data freshness is handled by the
        // mutation-settle effect, not by navigating.
        if (
          result.ok &&
          !isNavigateNoop(
            `${locationRef.current.pathname}${locationRef.current.search}`,
            result.url,
          )
        ) {
          try {
            navigateRef.current(result.url);
          } catch {
            // Still report the result below so the agent loop resumes.
          }
        }
        addToolResult({
          tool: 'navigate_to',
          toolCallId: toolCall.toolCallId,
          output: result,
        });
        return;
      }
      if (toolCall.toolName === READ_CURRENT_PAGE_TOOL) {
        let output: ReadCurrentPageOutput;
        try {
          output = readCurrentPage();
        } catch (error) {
          // Still answer the call, or the agent loop waits on it forever.
          captureException(error);
          output = { ok: false, error: 'The page could not be read.' };
        }
        addToolResult({
          tool: READ_CURRENT_PAGE_TOOL,
          toolCallId: toolCall.toolCallId,
          output,
        });
        return;
      }
      if (toolCall.toolName === CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL) {
        const { toolCallId } = toolCall;
        if (startedLayoutChecksRef.current.has(toolCallId)) {
          return;
        }
        startedLayoutChecksRef.current.add(toolCallId);
        const check = new AbortController();
        layoutChecksRef.current.set(toolCallId, check);
        setLayoutChecksRunning(true);
        void runTemplateLayoutCheck(
          toolCall.input,
          check.signal,
          () => latestMessagesRef.current,
          earlyLayoutChecksRef.current,
        ).then(output => {
          if (check.signal.aborted) {
            return;
          }
          layoutChecksRef.current.delete(toolCallId);
          setLayoutChecksRunning(layoutChecksRef.current.size > 0);
          void addToolResult({
            tool: CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL,
            toolCallId,
            output,
          });
        });
        return;
      }
      // `compose_email` is answered only by the user, from its card. Any
      // other tool is server-driven. `onToolCall` fires on the
      // `tool-input-available` chunk, which for an approval-gated tool arrives
      // *before* its `tool-approval-request` chunk — so responding here would
      // settle the call (e.g. graphql_mutation) before the user can approve or
      // deny it. Server-executed tools likewise get their result from the
      // server. A genuinely unknown *client* tool (a newer server deploy added
      // one this build doesn't implement) is handled once the stream settles
      // (see effect below) so the loop still can't stall.
    },
  });

  const isLoading = status === 'submitted' || status === 'streaming';
  const headerActions = headerActionsEnabled(status);
  const isBusy = isLoading || layoutChecksRunning;

  const haltGeneration = () => {
    userStoppedRef.current = true;
    void stop();
  };

  const dropLayoutChecks = () => {
    abortLayoutChecks(layoutChecksRef.current);
    earlyLayoutChecksRef.current.abortAll();
    startedLayoutChecksRef.current.clear();
    setLayoutChecksRunning(false);
  };

  const handleStop = () => {
    haltGeneration();
    const running = [...layoutChecksRef.current.keys()];
    abortLayoutChecks(layoutChecksRef.current);
    setLayoutChecksRunning(false);
    for (const toolCallId of running) {
      void addToolResult({
        tool: CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL,
        toolCallId,
        output: stoppedLayoutCheckOutput(),
      });
    }
  };

  const allowAgentResume = () => {
    userStoppedRef.current = false;
    interruptedAutoRetriesRef.current = 0;
  };
  const isWaiting = status === 'submitted';
  // Send time of the current turn (new message, regenerate, or the automatic
  // resume after tool results / approvals): the "Thinking…" timer runs from it.
  // Set during render, not in an effect, so the first frame of a turn never
  // shows the previous turn's time. A 'streaming' not preceded by 'submitted'
  // means that render was batched away.
  const [turnStartedAt, setTurnStartedAt] = React.useState<number | null>(null);
  const [prevStatus, setPrevStatus] = React.useState(status);
  if (status !== prevStatus) {
    setPrevStatus(status);
    if (
      status === 'submitted' ||
      (status === 'streaming' && prevStatus !== 'submitted')
    ) {
      setTurnStartedAt(Date.now());
    }
  }
  const turnsRef = React.useRef<Array<ChatTurn<(typeof messages)[number]>>>([]);
  const turns = React.useMemo(() => {
    turnsRef.current = groupChatTurns(messages, turnsRef.current);
    return turnsRef.current;
  }, [messages]);
  // The clarifying-questions call the settled last turn is paused on. It is
  // answered in the card docked above the input, not inside the transcript.
  const lastTurn = turns.at(-1);
  const pendingClarify =
    !isLoading && lastTurn != null && lastTurn.last.role !== 'user'
      ? findPendingClarifyPart(lastTurn.parts as Array<MessagePart>)
      : null;
  const pendingClarifyId = pendingClarify?.toolCallId ?? null;
  // The dock shrinks the scroll container, which useStickToBottom does not
  // observe (only the content): re-pin the view if it was at the bottom.
  const isAtBottomRef = React.useRef(isAtBottom);
  isAtBottomRef.current = isAtBottom;
  React.useEffect(() => {
    if (pendingClarifyId != null && isAtBottomRef.current) {
      scrollToBottom();
    }
  }, [pendingClarifyId, scrollToBottom]);
  const lastMessage = messages[messages.length - 1];
  // A turn that resumes an assistant message (after tool results / approvals)
  // shows its "Thinking…" inside that message; otherwise it sits below the
  // user's message until the reply exists.
  const resumesAssistant = lastMessage?.role === 'assistant';
  const activeChatId = messages.length > 0 ? chatId : null;

  const pendingMessagesRef = React.useRef<typeof messages | null>(null);
  const latestMessagesRef = React.useRef(messages);
  latestMessagesRef.current = messages;
  const latestChatIdRef = React.useRef(chatId);
  latestChatIdRef.current = chatId;
  // The mount fetch can still be in flight when /models returns. Until it
  // settles, sending would let the late setMessages(dbMessages) replace the
  // just-sent turn with the stale snapshot.
  const [historyReady, setHistoryReady] = React.useState(
    () => messagesRef.current.length > 0,
  );

  React.useEffect(() => {
    if (messagesRef.current.length > 0) {
      // Same as opening another chat: a dangling tool in this snapshot must
      // not start a model turn just because the panel mounted.
      userStoppedRef.current = true;
      setMessages(messagesRef.current as typeof messages);
      setHistoryReady(true);
    } else {
      // Server is the source of truth — load from DB. Capture the chat id the
      // fetch was issued for: if the user starts a new chat (or loads another
      // one from history) before the response lands, dropping the stale result
      // prevents it from overwriting the now-active chat's messages.
      const fetchedChatId = chatId;
      chatFetch(`${chatApiBase}/${fetchedChatId}`, {
        headers: getChatHeaders(),
      })
        // A 404 is the common fresh-session case (a sessionStorage chat id that
        // was never persisted); any other non-ok is a real error we must not
        // treat as "no model", or the reset below would overwrite a chat's
        // stored model on the next send. Mirror ChatHistory.handleSelect.
        .then(res =>
          res.ok ? res.json() : res.status === 404 ? { notFound: true } : null,
        )
        .then(data => {
          if (latestChatIdRef.current !== fetchedChatId || data == null) {
            return;
          }
          const dbMessages = data?.messages as typeof messages | undefined;
          if (dbMessages && dbMessages.length > 0) {
            userStoppedRef.current = true;
            setMessages(dbMessages);
          }
          const loadedModelId =
            typeof data?.model === 'string' ? data.model : null;
          if (loadedModelId) {
            selectLoadedModel(loadedModelId);
          } else {
            // New/legacy chat with no persisted model: use the admin default
            // rather than keeping the previously viewed chat's model.
            resetToDefaultModel();
          }
        })
        .catch(() => {})
        .finally(() => {
          setHistoryReady(true);
        });
    }

    return () => {
      messagesRef.current = latestMessagesRef.current;
      chatIdRef.current = latestChatIdRef.current;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (pendingMessagesRef.current != null) {
      setMessages(pendingMessagesRef.current);
      pendingMessagesRef.current = null;
    }
  }, [chatId, setMessages]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => focusInput(), []);

  React.useEffect(() => {
    const layoutChecks = layoutChecksRef.current;
    const earlyLayoutChecks = earlyLayoutChecksRef.current;
    return () => {
      abortLayoutChecks(layoutChecks);
      earlyLayoutChecks.abortAll();
    };
  }, []);

  React.useEffect(() => {
    if (!isLoading) {
      return;
    }
    const last = messages[messages.length - 1];
    if (last?.role === 'assistant') {
      earlyLayoutChecksRef.current.startFromParts(last.parts);
    }
  }, [isLoading, messages]);

  const handleNewChat = () => {
    haltGeneration();
    dropLayoutChecks();
    pendingMessagesRef.current = null;
    setMessages([]);
    const newId = crypto.randomUUID();
    setChatId(newId);
    chatIdRef.current = newId;
    writeSessionChatId(sessionUserId, newId);
    clearQueuedFiles();
    setAttachError(null);
    setAutoApprove(false);
    autoRespondedApprovalsRef.current.clear();
    unsupportedToolRespondedRef.current.clear();
    clarifyRespondedRef.current.clear();
    composeEmailRespondedRef.current.clear();
    resetToDefaultModel();
    setView('chat');
    focusInput();
  };

  const handleLoadChat = (
    loadedChatId: string,
    loadedMessages: Array<{
      id: string;
      role: 'user' | 'assistant';
      parts: UIMessage['parts'];
      metadata?: unknown;
    }>,
    loadedModelId?: string | null,
  ) => {
    haltGeneration();
    dropLayoutChecks();
    pendingMessagesRef.current = loadedMessages as typeof messages;
    messagesRef.current = loadedMessages as typeof messages;
    setChatId(loadedChatId);
    chatIdRef.current = loadedChatId;
    setAutoApprove(false);
    autoRespondedApprovalsRef.current.clear();
    unsupportedToolRespondedRef.current.clear();
    clarifyRespondedRef.current.clear();
    composeEmailRespondedRef.current.clear();
    if (loadedModelId) {
      selectLoadedModel(loadedModelId);
    } else {
      // Legacy chat with no persisted model: use the admin default rather than
      // keeping whatever the previously viewed chat had selected.
      resetToDefaultModel();
    }
    setView('chat');
    focusInput();
    setTimeout(() => scrollToBottom(), 0);
  };

  const handleAddFiles = async (newFiles: File[]) => {
    // Return focus to the textarea right away (covers every exit path below),
    // so the user can keep typing after the OS file dialog / a drop stole it.
    focusInput();
    const accepted = newFiles.filter(isAcceptedFile);
    if (accepted.length < newFiles.length) {
      setAttachError(
        t(
          'Some files were skipped. Allowed: images (png, jpeg, webp, gif) and PDF.',
        ),
      );
    } else {
      setAttachError(null);
    }
    if (accepted.length === 0) {
      return;
    }
    try {
      // Large images are downscaled client-side; files that cannot be made to
      // fit the server's payload caps come back as null and are dropped.
      const processed = await Promise.all(accepted.map(fileToFileUIPart));
      const attachments = processed.filter(
        (attachment): attachment is ProcessedAttachment => !!attachment,
      );
      const parts = attachments.map(attachment => attachment.part);
      if (parts.length < processed.length) {
        setAttachError(
          t('Attachments are too large. Keep the total under ~7MB.'),
        );
      }
      if (parts.length === 0) {
        return;
      }
      const next = [...files, ...parts];
      const validationError = validateAttachments(next, t);
      if (validationError) {
        setAttachError(validationError);
        return;
      }
      setFiles(next);
      for (const { part, original } of attachments) {
        if (original != null) {
          queuedOriginalsRef.current.set(part.attachmentId, original);
        }
      }
    } catch {
      setAttachError(t('Failed to read the selected files.'));
    }
  };

  const handleFilesChange = (next: ChatFileUIPart[]) => {
    const kept = new Set(next.map(file => file.attachmentId));
    for (const attachmentId of queuedOriginalsRef.current.keys()) {
      if (!kept.has(attachmentId)) {
        queuedOriginalsRef.current.delete(attachmentId);
      }
    }
    setFiles(next);
  };

  const clearQueuedFiles = () => {
    setFiles([]);
    queuedOriginalsRef.current.clear();
  };

  // Only sent photos, under the chat of that send, so removed photos leave
  // nothing behind. Held until the reply streams (see the status effect).
  const holdSentOriginals = (
    sentChatId: string,
    sentFiles: ChatFileUIPart[],
  ) => {
    originalUploads.hold(
      sentFiles.flatMap(file => {
        const original = queuedOriginalsRef.current.get(file.attachmentId);
        return original != null
          ? [
              {
                chatId: sentChatId,
                attachmentId: file.attachmentId,
                file: original,
              },
            ]
          : [];
      }),
    );
    queuedOriginalsRef.current.clear();
  };

  // A streaming or finished reply means POST /chat stored the chat row and the
  // resized copies, so chat-api accepts the originals. A send that fails
  // before that drops them.
  React.useEffect(() => {
    if (status === 'streaming' || status === 'ready') {
      void originalUploads.release();
    } else if (status === 'error') {
      originalUploads.drop();
    }
  }, [status, originalUploads]);

  const handleSubmit = () => {
    const text = input.trim();
    if (
      (!text && files.length === 0) ||
      isBusy ||
      models.length === 0 ||
      !historyReady
    ) {
      return;
    }
    // Scroll back down so the new message and its response are visible, even
    // if the user had scrolled up during a prior stream.
    scrollToBottom();
    setInput('');
    setFiles([]);
    setAttachError(null);
    allowAgentResume();
    if (text) {
      sendMessage({ text, files: files.length > 0 ? files : undefined });
    } else {
      sendMessage({ files });
    }
    holdSentOriginals(chatId, files);
    // Keep focus in the textarea when sending via the send button (Enter-key
    // submits already keep it naturally).
    focusInput();
  };

  // Empty-thread prompts. The visible label is the message the model sees.
  // Skills that shape the reply (created in the chat DB, not in this repo)
  // match that translated text.
  const sendStarterPrompt = (text: string) => {
    if (isBusy || models.length === 0 || !historyReady) {
      return;
    }
    scrollToBottom();
    setInput('');
    clearQueuedFiles();
    setAttachError(null);
    allowAgentResume();
    sendMessage({ text });
    focusInput();
  };

  // Editing a user message truncates the conversation at that message and
  // regenerates from the edited text (the server deletes the now-stale rows).
  const handleEditMessage = (messageId: string, text: string) => {
    if (isBusy) {
      return;
    }
    allowAgentResume();
    sendMessage({ text, messageId });
  };

  const handleApprovalResponse = (
    approvalId: string,
    approved: boolean,
    options?: { approveAll?: boolean },
  ) => {
    if (approved && options?.approveAll) {
      setAutoApprove(true);
    }
    allowAgentResume();
    void addToolApprovalResponse({ id: approvalId, approved });
  };

  const handleClarifyResolve: ClarifyResolveHandler = (toolCallId, output) => {
    if (clarifyRespondedRef.current.has(toolCallId)) {
      return;
    }
    clarifyRespondedRef.current.add(toolCallId);
    allowAgentResume();
    void addToolResult({
      tool: ASK_CLARIFYING_QUESTIONS_TOOL,
      toolCallId,
      output,
    });
  };

  // The user's answer to a compose_email card. Sending is a user action, so it
  // also lifts a Stop / reload hold, like answering an approval.
  const handleComposeEmailResult = (
    toolCallId: string,
    output: ComposeEmailOutput,
  ) => {
    if (composeEmailRespondedRef.current.has(toolCallId)) {
      return;
    }
    composeEmailRespondedRef.current.add(toolCallId);
    if (output.status === 'sent') {
      // The composer already emitted the feed refresh. The send went through
      // the email service, not Apollo, so the linked records' queries are stale.
      const part = latestMessagesRef.current
        .at(-1)
        ?.parts.find(
          candidate =>
            'toolCallId' in candidate && candidate.toolCallId === toolCallId,
        );
      refetchQueriesMentioning(
        apolloClient,
        part != null && 'input' in part
          ? composeEmailRefreshIds(part.input)
          : [],
      );
    }
    allowAgentResume();
    void addToolResult({ tool: COMPOSE_EMAIL_TOOL, toolCallId, output });
  };

  // Auto-approve pending mutation requests while "approve all" is active. Runs
  // once the stream has settled (addToolApprovalResponse only auto-resumes the
  // loop when the chat is not streaming/submitted) and marks each approval id
  // so it is answered exactly once. A user Stop leaves the card alone: answering
  // it here would show "approved" and then not run the change.
  React.useEffect(() => {
    if (!autoApprove || isLoading || !mayAutoApprove(userStoppedRef.current)) {
      return;
    }
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') {
      return;
    }
    for (const part of last.parts as Array<MessagePart>) {
      const toolName = isToolPart(part) ? toolNameOfPart(part) : '';
      if (
        isToolPart(part) &&
        part.state === 'approval-requested' &&
        part.approval?.id != null &&
        !autoRespondedApprovalsRef.current.has(part.approval.id) &&
        !(
          toolName === ADD_PROPERTY_IMAGES_TOOL_NAME &&
          (hasUnresolvedPropertyImageAttachments(
            part.input,
            messages as Array<{ parts?: Array<MessagePart> }>,
          ) ||
            waitsForOriginalUploads(part.input, pendingOriginalIds))
        )
      ) {
        autoRespondedApprovalsRef.current.add(part.approval.id);
        void addToolApprovalResponse({ id: part.approval.id, approved: true });
      }
    }
  }, [
    autoApprove,
    isLoading,
    messages,
    addToolApprovalResponse,
    pendingOriginalIds,
  ]);

  React.useEffect(() => {
    if (!isLoading) {
      return;
    }
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') {
      return;
    }
    const recordId = extractRecordId(locationRef.current.pathname);
    const { ids: refreshIds } = claimRefreshIds(
      last.parts as Array<MessagePart>,
      recordId,
      liveRefreshedCallsRef.current,
      isThemeWriteSuccess,
    );
    refetchQueriesMentioning(apolloClient, [...refreshIds]);
  }, [isLoading, messages, apolloClient]);

  // Refresh the record on screen after the chat wrote to it. Chat mutations run
  // on crm-chat-api -> Hasura, so the CRM Apollo cache never sees the write and
  // an open drawer keeps showing pre-mutation data. Once the stream settles,
  // look at the last assistant turn: for each successful mutation that mentions
  // the record in the current URL, refetch only the active queries whose
  // variables reference that record (drawer + cards), not everything mounted.
  // Pages without a record id (dashboard, lists) are never touched, so a
  // CMA-from-scratch turn does not reload an unrelated screen.
  React.useEffect(() => {
    if (isLoading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current) {
      // Initial mount, history load or chat switch: old parts replayed at once.
      return;
    }
    wasLoadingRef.current = false;

    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') {
      return;
    }
    const recordId = extractRecordId(locationRef.current.pathname);
    const { claimedAny: hadSuccessfulMutation, ids: refreshIds } =
      claimRefreshIds(
        last.parts as Array<MessagePart>,
        recordId,
        refreshedMutationCallsRef.current,
        isSuccessfulMutationPart,
        liveRefreshedCallsRef.current,
      );
    if (hadSuccessfulMutation) {
      // Feed/tasks refresh bus (ActivityFeed, DashboardTasksCard,
      // ListingBuyersDashboard). Emitted for ANY successful mutation — also on
      // pages without a record id, where the refetch below never runs (a task
      // created from /dashboard must still show up in DashboardTasksCard). On
      // record pages the feed query is usually also caught by refetchQueries
      // (its variables carry the record id); the bus is still needed because
      // only its handler resets the feed's reachedEnd pagination latch.
      emitFeedRefresh();
    }
    if (refreshIds.size === 0) {
      return;
    }
    refetchQueriesMentioning(apolloClient, [...refreshIds]);
  }, [isLoading, messages, apolloClient]);

  // A part still `input-available` after the stream stops will not receive a
  // result on this connection. Approval-gated tools and `navigate_to` stay
  // waiting. A server tool that runs on its own is marked interrupted so the
  // row stops spinning and the model can retry. An unknown client tool gets
  // the unsupported-version error. A user Stop sets `userStoppedRef` first, so
  // this settle does not start another turn. See `unsupportedClientTool`.
  React.useEffect(() => {
    if (isLoading) {
      return;
    }
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') {
      return;
    }
    const results = unsupportedClientToolResults(
      last.parts as Array<MessagePart>,
      unsupportedToolRespondedRef.current,
    );
    if (results.length === 0) {
      return;
    }
    // Each addToolResult checks resume before it returns. Await them in order
    // so the last tool is marked before the retry is counted. Counting after
    // the first tool would spend the retry while another tool is still waiting.
    void (async () => {
      for (const result of results) {
        unsupportedToolRespondedRef.current.add(result.toolCallId);
        await addToolResult(result);
      }
      interruptedAutoRetriesRef.current = interruptedRetriesAfterSettle(
        messagesWithSettledResults(
          messages as Parameters<typeof messagesWithSettledResults>[0],
          results,
        ),
        userStoppedRef.current,
        interruptedAutoRetriesRef.current,
      );
    })();
  }, [isLoading, messages, addToolResult]);

  React.useEffect(() => {
    if (isLoading) {
      return;
    }
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') {
      return;
    }
    for (const toolCallId of unsettledLayoutCheckIds(
      last.parts,
      startedLayoutChecksRef.current,
    )) {
      startedLayoutChecksRef.current.add(toolCallId);
      void addToolResult({
        tool: CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL,
        toolCallId,
        output: stoppedLayoutCheckOutput(),
      });
    }
  }, [isLoading, messages, addToolResult]);

  // Drag-and-drop attachments: the whole panel is a drop target. dragenter and
  // dragleave fire for every child element, so a depth counter tells apart
  // "left the panel" from "moved between children".
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  const dragDepthRef = React.useRef(0);
  const dragHasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer.types).includes('Files');

  // preventDefault runs on every view — on the history view it swallows the
  // drop instead of letting the browser navigate away to the dropped file.
  // Only the chat view shows the overlay and accepts the attachment.
  // The panel is marked NESTED_FILE_DROPZONE_ATTR so document.body
  // FullScreenDropZones (CMA photos/cover, listing media, …) release the
  // drag and do not upload when the pointer is over chat.
  const handleDragEnter = (e: React.DragEvent) => {
    if (!dragHasFiles(e)) {
      return;
    }
    e.preventDefault();
    if (view !== 'chat') {
      return;
    }
    dragDepthRef.current += 1;
    setIsDraggingOver(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!dragHasFiles(e)) {
      return;
    }
    // Required for the drop event to fire.
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!dragHasFiles(e) || view !== 'chat') {
      return;
    }
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!dragHasFiles(e)) {
      return;
    }
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDraggingOver(false);
    if (view !== 'chat') {
      return;
    }
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) {
      void handleAddFiles(dropped);
    }
  };

  // A view change mid-drag would strand the overlay: the early returns above
  // stop maintaining the depth counter once the chat view is gone.
  React.useEffect(() => {
    if (view !== 'chat') {
      dragDepthRef.current = 0;
      setIsDraggingOver(false);
    }
  }, [view]);

  // Best-effort thumbs up/down, forwarded to Langfuse via the chat API.
  const handleFeedback = (messageId: string, rating: 'up' | 'down') => {
    void chatFetch(`${chatApiBase}/${chatId}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getChatHeaders() },
      body: JSON.stringify({ messageId, rating }),
    }).catch(() => {});
  };

  const lastAssistantId = [...messages]
    .reverse()
    .find(message => message.role === 'assistant')?.id;

  return (
    <div
      {...{ [NESTED_FILE_DROPZONE_ATTR]: '' }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      css={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.superLightBlue,
        height: '100%',
        overflow: 'hidden',
        minHeight: 0,
        position: 'relative',
      }}
    >
      {isDraggingOver && (
        <div
          css={{
            position: 'absolute',
            inset: 6,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            borderRadius: 12,
            border: `2px dashed ${colors.primaryMain}`,
            backgroundColor: colors.superLightBlue,
            opacity: 0.95,
            color: colors.primaryMain,
            // Let the underlying panel receive the drop/dragleave events.
            pointerEvents: 'none',
          }}
        >
          <Paperclip size={24} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('Drop files to attach')}
          </Typography>
        </div>
      )}
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          p: isMobile ? '0 4px 0 12px' : '0 4px 0 16px',
          borderBottom: `1px solid ${colors.divider}`,
          minHeight: isMobile ? 48 : TOP_BAR_HEIGHT,
          flexShrink: 0,
          backgroundColor: colors.white,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, fontSize: '1rem' }}
        >
          {t('Aura AI Assistant')}
        </Typography>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0 }}>
          <IconButton
            size="small"
            onClick={handleNewChat}
            title={t('New chat')}
            disabled={!headerActions.newChat || layoutChecksRunning}
          >
            <SquarePen size={20} color={colors.black} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setView(v => (v === 'history' ? 'chat' : 'history'))}
            title={t('Chat history')}
            disabled={!headerActions.history || layoutChecksRunning}
            aria-pressed={view === 'history'}
          >
            <History
              size={20}
              color={view === 'history' ? colors.primaryMain : colors.black}
            />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setView(v => (v === 'files' ? 'chat' : 'files'))}
            title={t('My files')}
            disabled={!headerActions.files}
            aria-pressed={view === 'files'}
          >
            <FolderOpen
              size={20}
              color={view === 'files' ? colors.primaryMain : colors.black}
            />
          </IconButton>
          <IconButton
            size="small"
            onClick={onClose}
            title={t('Close panel (⌘J)')}
          >
            <X size={20} color={colors.black} />
          </IconButton>
        </Stack>
      </Stack>

      {view === 'history' ? (
        <ChatHistory
          activeChatId={activeChatId}
          onSelectChat={handleLoadChat}
          onNewChat={handleNewChat}
          onBack={() => setView('chat')}
        />
      ) : view === 'files' ? (
        <ChatFiles
          activeChatId={activeChatId}
          onSelectChat={handleLoadChat}
          canSwitchChat={headerActions.history}
          refreshKey={settledToolResultCount(
            messages as Array<{ role: string; parts?: Array<MessagePart> }>,
          )}
          chatWaitsForUser={waitsForUser(
            messages as Array<{ role: string; parts?: Array<MessagePart> }>,
          )}
          onBack={() => setView('chat')}
        />
      ) : (
        <ChatTableViewerProvider containerRef={tableContainerRef}>
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overscrollBehaviorY: 'contain',
              p: isMobile ? '12px 10px' : '16px 12px',
              position: 'relative',
            }}
          >
            <div ref={contentRef}>
              {modelsError && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  {modelsError}
                </Alert>
              )}
              {messages.length === 0 && (
                <Stack
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: colors.grey400,
                    textAlign: 'center',
                    p: 4,
                  }}
                >
                  <Compass size={40} />
                  <Typography
                    variant="body2"
                    sx={{ mt: 1.5, color: colors.grey500 }}
                  >
                    {t('Ask Aura anything')}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isBusy || models.length === 0 || !historyReady}
                    onClick={() =>
                      sendStarterPrompt(t('What can Aura do for me?'))
                    }
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      color: colors.primaryMain,
                      borderColor: colors.divider,
                    }}
                  >
                    {t('What can Aura do for me?')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isBusy || models.length === 0 || !historyReady}
                    onClick={() =>
                      sendStarterPrompt(t('What should I focus on today?'))
                    }
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      color: colors.primaryMain,
                      borderColor: colors.divider,
                    }}
                  >
                    {t('What should I focus on today?')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isBusy || models.length === 0 || !historyReady}
                    onClick={() =>
                      sendStarterPrompt(
                        t('How can I increase my online visibility?'),
                      )
                    }
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      color: colors.primaryMain,
                      borderColor: colors.divider,
                    }}
                  >
                    {t('How can I increase my online visibility?')}
                  </Button>
                </Stack>
              )}

              {/* Lets the add_property_images tool card resolve stable
                  attachment IDs to exact preview thumbnails, and the visual
                  cards fetch their image bytes for Copy. */}
              <ChatTurnStartContext.Provider value={turnStartedAt}>
                <VisualChatIdProvider value={activeChatId}>
                  <ChatAttachmentPreviewProvider
                    messages={messages as Array<{ parts?: Array<MessagePart> }>}
                    pendingOriginalIds={pendingOriginalIds}
                  >
                    {turns.map((turn, index) => {
                      const message = turn.last;
                      const isUser = message.role === 'user';
                      const isLast = index === turns.length - 1;
                      const isLastAssistant = !isUser && isBusy && isLast;
                      const parts = turn.parts as Array<MessagePart>;
                      // A turn paused on a mutation approval, an open clarifying
                      // questions card or an open email draft has status
                      // 'ready', but it is still in flight — regenerate/feedback
                      // on it would silently abandon the pending decision.
                      const hasPendingApproval =
                        !isUser &&
                        isLast &&
                        (pendingClarify != null ||
                          layoutChecksRunning ||
                          parts.some(
                            part =>
                              (isToolPart(part) &&
                                part.state === 'approval-requested') ||
                              isPendingComposeEmailPart(part),
                          ));
                      return (
                        <div
                          key={turn.key}
                          css={{
                            marginBottom: 16,
                            '&:last-of-type': { marginBottom: 0 },
                          }}
                        >
                          <ChatMessage
                            parts={parts}
                            isUser={isUser}
                            isStreaming={isLastAssistant}
                            // Approvals can only be pending on the in-flight last
                            // assistant message; older frozen approval parts must
                            // not render actionable buttons.
                            onApprovalResponse={
                              !isUser && isLast
                                ? handleApprovalResponse
                                : undefined
                            }
                            isLastTurn={!isUser && isLast}
                            isLoading={isLast && isLoading}
                            // Same for an open email draft: the AI SDK only
                            // records a tool result on the last message.
                            onComposeEmailResult={
                              !isUser && isLast
                                ? handleComposeEmailResult
                                : undefined
                            }
                            isMobile={isMobile}
                            onEditSubmit={
                              isUser
                                ? text => handleEditMessage(message.id, text)
                                : undefined
                            }
                            canEdit={isUser && !isBusy}
                          />
                          {!isUser &&
                          !isLastAssistant &&
                          !isWaiting &&
                          !hasPendingApproval ? (
                            <ChatMessageActions
                              messageText={getMessageText(parts)}
                              metadata={
                                message.metadata as
                                  | ChatMessageMetadata
                                  | undefined
                              }
                              canRegenerate={
                                message.id === lastAssistantId && !isBusy
                              }
                              onRegenerate={() => {
                                allowAgentResume();
                                void regenerate();
                              }}
                              onFeedback={rating =>
                                handleFeedback(message.id, rating)
                              }
                            />
                          ) : null}
                          {!isUser &&
                          !isLastAssistant &&
                          isCutOffReply(message.metadata) ? (
                            <Typography
                              variant="caption"
                              component="p"
                              sx={{ mt: 0.5, color: colors.grey500 }}
                            >
                              {t(
                                'This reply was cut off because it was too long. Ask me to continue, or to split the work into smaller parts.',
                              )}
                            </Typography>
                          ) : null}
                        </div>
                      );
                    })}
                  </ChatAttachmentPreviewProvider>
                </VisualChatIdProvider>

                {isLoading && !resumesAssistant && <ThinkingIndicator />}
              </ChatTurnStartContext.Provider>

              {attachError && (
                <Alert
                  severity="warning"
                  sx={{ mt: 1 }}
                  onClose={() => setAttachError(null)}
                >
                  {attachError}
                </Alert>
              )}

              {error && (
                <Alert
                  severity="error"
                  sx={{ mt: 1 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        allowAgentResume();
                        void regenerate();
                      }}
                    >
                      {t('Retry')}
                    </Button>
                  }
                >
                  {isTooManyPhotosError(error.message) && (
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {t(
                        'This conversation has too many photos for the AI model. Start a new chat to continue.',
                      )}
                    </Typography>
                  )}
                  <ChatErrorText message={error.message} />
                </Alert>
              )}
            </div>

            {!isAtBottom && (
              <IconButton
                onClick={() => scrollToBottom()}
                size="small"
                title={t('Scroll to bottom')}
                sx={{
                  position: 'sticky',
                  bottom: 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1,
                  backgroundColor: colors.white,
                  border: `1px solid ${colors.divider}`,
                  boxShadow: 2,
                  width: 32,
                  height: 32,
                  '&:hover': { backgroundColor: colors.grey100 },
                }}
              >
                <ChevronDown size={18} color={colors.black} />
              </IconButton>
            )}
          </Box>

          {autoApprove && (
            <Alert
              severity="warning"
              sx={{ mx: '12px', mb: 1, py: 0, alignItems: 'center' }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setAutoApprove(false)}
                >
                  {t('Turn off')}
                </Button>
              }
            >
              {t('Auto-approving data changes in this chat')}
            </Alert>
          )}

          {pendingClarify != null && pendingClarifyId != null && (
            <ClarifyingQuestionsDock
              key={pendingClarifyId}
              part={pendingClarify}
              onClarifyResolve={handleClarifyResolve}
            />
          )}

          <ChatInput
            ref={inputRef}
            input={input}
            onInputChange={setInput}
            onSubmit={handleSubmit}
            onStop={handleStop}
            isLoading={isBusy}
            modelsAvailable={models.length > 0 && historyReady}
            files={files}
            onFilesChange={handleFilesChange}
            onAddFiles={fs => void handleAddFiles(fs)}
            footer={
              <ModelSelector
                models={models}
                selectedModelId={selectedModelId}
                onModelChange={handleModelChange}
                isLoading={modelsLoading}
              />
            }
          />
        </ChatTableViewerProvider>
      )}
    </div>
  );
};
